import { z } from "zod";

// ---------------------------------------------------------------------------
// Sales playbook objectives
//
// Structured coaching objectives ("push high-margin add-ons on drywall
// calls") stored at Businesses/{businessId}/playbookObjectives/{id}.
// Business-wide (salesTeamId null) or per-team, optionally linked to a KPI
// definition so generated insights can quantify against a real target.
// The member-performance generator injects active objectives into its prompt;
// pinned "goal" memory remains the free-text fallback channel.
// ---------------------------------------------------------------------------

export const PLAYBOOK_MAX_ACTIVE_OBJECTIVES = 10;

export const PlaybookObjectiveStatus = z.enum(["active", "archived"]);
export type PlaybookObjectiveStatus = z.infer<typeof PlaybookObjectiveStatus>;

// ---------------------------------------------------------------------------
// Change experiments
//
// A playbook objective becomes an experiment when it carries a time window:
// "we're trying X until date Y". The nightly `ai-experiment-verdict` generator
// evaluates the linked KPI before vs during the window once it has closed,
// writes one persist verdict insight, stamps `evaluatedAt`/`verdictInsightId`
// exactly once, and archives the objective - the verdict insight plus the
// archived entry are the permanent record.
// ---------------------------------------------------------------------------

/** Baseline depth (ended periods before the window) used by default. */
export const EXPERIMENT_DEFAULT_BASELINE_PERIODS = 3;
/** Longest allowed experiment window. */
export const EXPERIMENT_MAX_WINDOW_DAYS = 120;

export interface PlaybookExperiment {
  /** ISO date (YYYY-MM-DD), first day of the change window. */
  startsAt: string;
  /** ISO date, last day of the window (inclusive). */
  endsAt: string;
  /** Ended periods before `startsAt` used as the baseline. */
  baselinePeriods: number;
  /** Stamped exactly once by the verdict generator. */
  evaluatedAt: string | null;
  /** dedupeKey of the verdict insight, once written. */
  verdictInsightId: string | null;
}

export type ExperimentPhase = "upcoming" | "running" | "ended" | "evaluated";

/** Where an experiment is in its lifecycle, judged on the date (UTC). */
export function experimentPhase(
  experiment: Pick<PlaybookExperiment, "startsAt" | "endsAt" | "evaluatedAt">,
  now: Date = new Date(),
): ExperimentPhase {
  if (experiment.evaluatedAt != null) return "evaluated";
  const today = now.toISOString().slice(0, 10);
  if (today < experiment.startsAt) return "upcoming";
  if (today <= experiment.endsAt) return "running";
  return "ended";
}

export interface PlaybookObjectiveRecord {
  id: string;
  businessId: string;
  /** Null = applies to the whole organization. */
  salesTeamId: string | null;
  title: string;
  /** The concrete behavior to coach toward, phrased for use in calls. */
  behavior: string;
  rationale: string | null;
  /** Active KPI definition this objective moves, if any. */
  linkedKpiDefinitionId: string | null;
  /** Product names/hints the behavior concerns, e.g. ["gipsskruv"]. */
  productHints: string[];
  /** Set when the objective runs as a time-windowed change experiment. */
  experiment: PlaybookExperiment | null;
  status: PlaybookObjectiveStatus;
  createdBy: string;
  createdAt: string | null;
  updatedAt: string | null;
  archivedAt: string | null;
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Client-settable experiment window. `evaluatedAt`/`verdictInsightId` are
 * server-managed and never accepted from the client.
 */
export const UpsertPlaybookExperimentInput = z.object({
  startsAt: z.string().regex(ISO_DATE),
  endsAt: z.string().regex(ISO_DATE),
  baselinePeriods: z
    .number()
    .int()
    .min(1)
    .max(6)
    .default(EXPERIMENT_DEFAULT_BASELINE_PERIODS),
});
export type UpsertPlaybookExperimentInput = z.infer<
  typeof UpsertPlaybookExperimentInput
>;

export const UpsertPlaybookObjectiveInput = z
  .object({
    businessId: z.string().min(1),
    /** Omit to create. */
    objectiveId: z.string().min(1).optional(),
    salesTeamId: z.string().min(1).nullable().default(null),
    title: z.string().min(1).max(80),
    behavior: z.string().min(1).max(400),
    rationale: z.string().max(300).nullable().default(null),
    linkedKpiDefinitionId: z.string().min(1).nullable().default(null),
    productHints: z.array(z.string().min(1).max(60)).max(10).default([]),
    experiment: UpsertPlaybookExperimentInput.nullable().default(null),
  })
  .superRefine((input, ctx) => {
    if (input.experiment == null) return;
    const { startsAt, endsAt } = input.experiment;
    if (endsAt <= startsAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["experiment", "endsAt"],
        message: "Experimentets slutdatum måste vara efter startdatumet",
      });
    }
    const windowDays = (Date.parse(endsAt) - Date.parse(startsAt)) / 86_400_000;
    if (windowDays > EXPERIMENT_MAX_WINDOW_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["experiment", "endsAt"],
        message: `Ett experiment kan pågå högst ${EXPERIMENT_MAX_WINDOW_DAYS} dagar`,
      });
    }
    if (input.linkedKpiDefinitionId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["experiment"],
        message: "Ett experiment kräver en kopplad KPI",
      });
    }
  });
export type UpsertPlaybookObjectiveInput = z.infer<
  typeof UpsertPlaybookObjectiveInput
>;

export const ListPlaybookObjectivesInput = z.object({
  businessId: z.string().min(1),
  includeArchived: z.boolean().default(false),
});
export type ListPlaybookObjectivesInput = z.infer<
  typeof ListPlaybookObjectivesInput
>;

export const GetPlaybookProgressInput = z.object({
  businessId: z.string().min(1),
});
export type GetPlaybookProgressInput = z.infer<typeof GetPlaybookProgressInput>;

export const ArchivePlaybookObjectiveInput = z.object({
  businessId: z.string().min(1),
  objectiveId: z.string().min(1),
});
export type ArchivePlaybookObjectiveInput = z.infer<
  typeof ArchivePlaybookObjectiveInput
>;
